import React, { useMemo } from "react";
import _ from "lodash";

import theme from "theme";
import { colors } from "theme/palette";
import { formatNumberToString } from "utils/common";

import Box from "components/Box";
import DonutChart, { Legend } from "components/DonutChart";
import Stack from "components/Stack";

import { ProformaHeaderTypeTotal } from "entities/Proforma/components/ProformaContentHeader";
import ProformaPaper from "entities/Proforma/components/ProformaPaper";
import ProformaTable from "entities/Proforma/components/ProformaTable";
import { IIncomeOperatingCost } from "entities/Proforma/sdk";

interface IIncomeTable {
  incomeOperatingCost: IIncomeOperatingCost;
  totalUnits: number;
}

const headerIncomeAndOpex: Array<{ key: string; name: string }> = [
  { key: "name", name: "REVENUES" },
  { key: "total", name: "YEARLY TOTAL" },
  { key: "perunit", name: "PER UNIT" },
];

const IncomeTable: React.FC<IIncomeTable> = ({
  incomeOperatingCost,
  totalUnits,
}) => {
  const incomeAndOpexDetails = useMemo(() => {
    const rentalIncome = _.orderBy(
      _.map(incomeOperatingCost.rental_income, (value, key) => {
        return {
          name: key,
          total: formatNumberToString({ number: value.value }),
          perunit: formatNumberToString({ number: value.value / totalUnits }),
          position: value.position,
        };
      }),
      "position"
    );
    const lessIncome = _.orderBy(
      _.map(incomeOperatingCost.less_income, (value, key) => {
        return {
          name: key,
          total: formatNumberToString({ number: value.value }),
          perunit: formatNumberToString({ number: value.value / totalUnits }),
          position: value.position,
        };
      }),
      "position"
    );

    const otherIncome = _.orderBy(
      _.map(incomeOperatingCost.other_income, (value, key) => {
        return {
          name: key,
          total: formatNumberToString({ number: value.value }),
          perunit: formatNumberToString({ number: value.value / totalUnits }),
          position: value.position,
        };
      }),
      "position"
    );

    const otherData = [
      {
        name: "Rental Income",
        total: formatNumberToString({
          number: incomeOperatingCost.net_rental_income.value,
        }),
        perunit: formatNumberToString({
          number: incomeOperatingCost.net_rental_income?.value / totalUnits,
        }),
      },
      {
        name: "Other Income",
        total: formatNumberToString({
          number: incomeOperatingCost.total_other_income.value,
        }),
        perunit: formatNumberToString({
          number: incomeOperatingCost.total_other_income.value / totalUnits,
        }),
        detail: otherIncome,
      },
      {
        name: "Gross Potential Income (GPI)",
        total: formatNumberToString({
          number: incomeOperatingCost.total_gross_expenses.value,
        }),
        perunit: formatNumberToString({
          number: incomeOperatingCost.total_gross_expenses.value / totalUnits,
        }),
      },
      {
        name: "Effective Gross Income (EGI)",
        total: formatNumberToString({
          number: incomeOperatingCost.total_effective_gross_income.value,
        }),
        perunit: formatNumberToString({
          number:
            incomeOperatingCost.total_effective_gross_income.value / totalUnits,
        }),
      },
    ];

    return [...rentalIncome, ...lessIncome, ...otherData];
  }, [incomeOperatingCost, totalUnits]);

  const totals = [
    {
      label: "Effective Gross Income",
      value: incomeOperatingCost.total_effective_gross_income.value,
      type: ProformaHeaderTypeTotal.PRICE,
    },
    {
      label: "Per Unit",
      value:
        incomeOperatingCost.total_effective_gross_income?.value / totalUnits,
      type: ProformaHeaderTypeTotal.PRICE,
    },
  ];

  const donutChartData = [
    {
      value: incomeOperatingCost.net_rental_income.value,
      label: `Net Rental Income: ${formatNumberToString({
        number: incomeOperatingCost.net_rental_income.value,
      })}`,
      color: colors.teal100,
    },
    {
      value: incomeOperatingCost.total_other_income.value,
      label: `All Other Income: ${formatNumberToString({
        number: incomeOperatingCost.total_other_income.value,
      })}`,
      color: colors.teal80,
    },
  ];

  return (
    <ProformaPaper title="Income" subTitle="Revenues Breakdown" totals={totals}>
      <Stack direction="row">
        <Stack
          sx={{ flex: 1, paddingTop: theme.spacing(12) }}
          alignItems="center"
        >
          <Stack spacing={2}>
            <DonutChart data={donutChartData} width={200} />
            <Legend data={donutChartData} />
          </Stack>
        </Stack>
        <Box
          sx={{
            flex: 1,
            margin: theme.spacing(3, 0),
          }}
        >
          <ProformaTable
            rows={incomeAndOpexDetails}
            headers={headerIncomeAndOpex}
          />
        </Box>
      </Stack>
    </ProformaPaper>
  );
};

export default IncomeTable;
